import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useTeams } from "../lib/teams";
import { usePlan } from "../lib/usePlan";
import api from "../lib/api";
import { trackBeginCheckout } from "../lib/gtag";
import { CheckCircle2, AlertCircle, Sparkles, ArrowRight, XCircle, Calendar, Users, Layers, Gift, Copy, Share2 } from "lucide-react";
import { toast } from "sonner";

const STATUS_META = {
  free: { label: "Piano Free", color: "bg-slate-100 text-slate-700", Icon: Layers },
  trialing: { label: "In trial gratuito", color: "bg-sky-100 text-sky-700", Icon: Sparkles },
  active: { label: "Attivo", color: "bg-emerald-100 text-emerald-700", Icon: CheckCircle2 },
  canceled: { label: "Annullato", color: "bg-amber-100 text-amber-700", Icon: AlertCircle },
  past_due: { label: "Scaduto", color: "bg-red-100 text-red-700", Icon: XCircle },
};

const formatDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" }) : "—";

const daysLeft = (iso) => {
  if (!iso) return null;
  const d = Math.ceil((new Date(iso) - new Date()) / (1000 * 60 * 60 * 24));
  return d;
};

export default function BillingPage() {
  const { activeTeam } = useTeams();
  const plan = usePlan();
  const navigate = useNavigate();
  const [canceling, setCanceling] = useState(false);

  useEffect(() => { plan.reload?.(); /* eslint-disable-next-line */ }, [activeTeam?.id]);

  if (!activeTeam) return <p className="text-slate-500">Seleziona una squadra.</p>;
  if (plan.loading) return <p className="text-slate-500">Caricamento...</p>;

  const status = plan.status || "free";
  const meta = STATUS_META[status] || STATUS_META.free;
  const Icon = meta.Icon;
  const info = plan.info || {};
  const dLeft = daysLeft(plan.periodEnd);

  const onCancel = async () => {
    if (!window.confirm("Annullare il rinnovo automatico? L'accesso continuerà fino alla scadenza del periodo corrente.")) return;
    setCanceling(true);
    try {
      await api.post("/billing/cancel", { team_id: activeTeam.id });
      toast.success("Rinnovo annullato. Accesso valido fino alla scadenza.");
      await plan.reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    }
    setCanceling(false);
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="billing-page">
      <div>
        <div className="label-eyebrow">Abbonamento</div>
        <h1 className="font-heading text-4xl font-bold tracking-tight mt-1">Il mio piano</h1>
        <p className="text-slate-500 mt-1">Gestisci l'abbonamento per la squadra <strong>{activeTeam.name}</strong>.</p>
      </div>

      <Card className="p-6 border-slate-200">
        <div className="flex items-start gap-4">
          <div className={`w-12 h-12 rounded-2xl grid place-items-center ${meta.color}`}>
            <Icon size={22} />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h2 className="font-heading font-bold text-2xl">{info.name || "Free"}</h2>
              <span className={`text-[11px] font-bold uppercase tracking-wider px-2 py-1 rounded-full ${meta.color}`}>{meta.label}</span>
              {plan.cancelAtPeriodEnd && (
                <span className="text-[11px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-amber-100 text-amber-700">
                  Rinnovo annullato
                </span>
              )}
            </div>
            <p className="text-sm text-slate-600 mt-1">{info.tagline}</p>

            {plan.periodEnd && status !== "free" && (
              <div className="mt-3 flex items-center gap-2 text-sm">
                <Calendar size={14} className="text-slate-400" />
                <span className="text-slate-600">
                  {plan.cancelAtPeriodEnd ? "Scadenza" : status === "trialing" ? "Fine trial" : "Prossimo rinnovo"}: <strong>{formatDate(plan.periodEnd)}</strong>
                  {dLeft !== null && dLeft >= 0 && <span className="text-slate-500"> · {dLeft} giorni rimanenti</span>}
                </span>
              </div>
            )}

            {info.price_eur > 0 && (
              <div className="mt-1 text-sm text-slate-500">
                Prezzo:{" "}
                <strong>
                  €{Number(info.price_eur).toFixed(2).replace(".", ",")}/mese
                </strong>
                {info.price_yearly_eur && (
                  <span className="text-slate-400">
                    {" "}· oppure €{info.price_yearly_eur}/anno (-20%)
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-6 border-t pt-5">
          <UsageStat icon={Users} label="Giocatori" value={plan.usage?.players ?? 0} limit={info.players_limit} />
          <UsageStat icon={Layers} label="Squadre" value={plan.usage?.teams_owned ?? 0} limit={info.teams_limit} />
        </div>

        <div className="flex gap-2 flex-wrap mt-6">
          <Link to="/app/pricing">
            <Button className="bg-sky-500 hover:bg-sky-600 text-white" data-testid="upgrade-from-billing">
              <Sparkles size={14} className="mr-1" /> {status === "free" ? "Scegli un piano" : "Cambia piano"} <ArrowRight size={14} className="ml-1" />
            </Button>
          </Link>
          {status !== "free" && !plan.cancelAtPeriodEnd && (
            <Button variant="outline" onClick={onCancel} disabled={canceling} data-testid="cancel-subscription">
              <XCircle size={14} className="mr-1" /> Annulla rinnovo
            </Button>
          )}
          {plan.adminBypass && (
            <Link to="/app/admin/billing/lifecycle">
              <Button variant="outline" data-testid="admin-lifecycle-link">
                Lifecycle (admin) <ArrowRight size={14} className="ml-1" />
              </Button>
            </Link>
          )}
        </div>
      </Card>

      {/* Stripe Subscriptions (real recurring billing) */}
      <StripeSubscriptionsCard team={activeTeam} status={status} />

      <Card className="p-6 border-slate-200">
        <h3 className="font-heading font-bold text-lg mb-3">Cosa include il tuo piano</h3>
        <div className="grid sm:grid-cols-2 gap-2 text-sm">
          {(info.feature_list || []).map((f, i) => (
            <div key={i} className="flex items-start gap-2">
              <CheckCircle2 size={14} className="text-emerald-500 shrink-0 mt-0.5" />
              <span className="text-slate-700">{f}</span>
            </div>
          ))}
        </div>
        {(info.missing || []).length > 0 && (
          <>
            <div className="mt-4 pt-4 border-t">
              <p className="text-xs uppercase tracking-wider text-amber-600 font-semibold mb-2">Sblocchi disponibili</p>
              <div className="grid sm:grid-cols-2 gap-2 text-sm">
                {info.missing.map((f, i) => (
                  <div key={i} className="flex items-start gap-2 text-slate-500">
                    <XCircle size={14} className="text-slate-300 shrink-0 mt-0.5" />
                    <span>{f}</span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </Card>

      {plan.adminBypass && (
        <Card className="p-4 border-amber-200 bg-amber-50 text-amber-800 text-sm">
          <strong>Modalità admin:</strong> tutti i limiti di piano sono disattivati per il tuo account.
        </Card>
      )}

      <ReferralCard team={activeTeam} />

      <p className="text-xs text-slate-400 text-center py-4">
        Hai domande? Scrivi a <a href="mailto:supporto@calcioclub.it" className="underline">supporto@calcioclub.it</a>
      </p>
    </div>
  );
}

function ReferralCard({ team }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!team?.id) return;
    api.get(`/teams/${team.id}/referral`).then(({ data }) => setData(data)).catch(() => {});
  }, [team?.id]);
  if (!data) return null;
  const shareUrl = `${window.location.origin}/pricing?ref=${data.code}`;
  const copy = (text, msg) => {
    navigator.clipboard?.writeText(text);
    toast.success(msg);
  };
  const shareNative = async () => {
    const message = `Ciao! Ti invito a provare Calcio Club Manager — usa il codice ${data.code} su ${shareUrl} per ricevere il 50% di sconto sul primo mese 🎁`;
    if (navigator.share) {
      try { await navigator.share({ title: "Calcio Club Manager", text: message, url: shareUrl }); } catch (e) {}
    } else {
      copy(message, "Messaggio copiato — incollalo dove vuoi");
    }
  };
  return (
    <Card className="p-6 bg-gradient-to-br from-amber-50 via-white to-emerald-50 border-amber-200" data-testid="referral-card">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-2xl bg-amber-100 text-amber-700 grid place-items-center shrink-0">
          <Gift size={22} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-heading font-bold text-lg">Invita un'altra società, guadagna 1 mese gratis</h3>
          <p className="text-sm text-slate-600 mt-1">
            Condividi il tuo codice. Quando una nuova società lo usa per abbonarsi, riceve uno sconto del{" "}
            <strong>{data.referee_discount_pct}%</strong> sul primo mese e tu ottieni{" "}
            <strong>+{data.reward_days} giorni gratis</strong> sul tuo piano.
          </p>
          <div className="mt-4 grid sm:grid-cols-[1fr_auto_auto] gap-2 items-center">
            <div className="flex-1 px-4 py-3 rounded-xl border border-amber-300 bg-white font-mono text-lg font-bold tracking-wider text-center sm:text-left">
              {data.code}
            </div>
            <Button onClick={() => copy(data.code, "Codice copiato")} variant="outline" className="bg-white" data-testid="copy-referral-code">
              <Copy size={13} className="mr-1" /> Copia codice
            </Button>
            <Button onClick={shareNative} className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="share-referral">
              <Share2 size={13} className="mr-1" /> Condividi
            </Button>
          </div>
          <div className="mt-2 text-xs text-slate-500 break-all">
            Link: <span className="font-mono">{shareUrl}</span>
          </div>
          {data.history.length > 0 && (
            <div className="mt-4 pt-4 border-t border-amber-200">
              <p className="text-xs font-semibold text-amber-700 uppercase tracking-wider mb-2">
                {data.history.length} societ{data.history.length === 1 ? "à" : "à"} invitate — {data.history.length} mes{data.history.length === 1 ? "e" : "i"} guadagnat{data.history.length === 1 ? "o" : "i"} 🎉
              </p>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

function UsageStat({ icon: Icon, label, value, limit }) {  const unlimited = limit === -1;
  const pct = unlimited || !limit ? 0 : Math.min(100, Math.round((value / limit) * 100));
  const danger = !unlimited && pct >= 80;
  return (
    <div className="p-3 rounded-xl border border-slate-200 bg-slate-50/40">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500">
        <Icon size={12} /> {label}
      </div>
      <div className="font-heading font-bold text-2xl mt-1">
        {value}
        <span className="text-slate-400 text-base font-normal">
          {" "}/ {unlimited ? "∞" : limit}
        </span>
      </div>
      {!unlimited && (
        <div className="mt-2 h-1.5 rounded-full bg-slate-200 overflow-hidden">
          <div
            className={`h-full rounded-full ${danger ? "bg-red-500" : "bg-sky-500"} transition-all`}
            style={{ width: `${pct}%` }}
          />
        </div>
      )}
    </div>
  );
}


function StripeSubscriptionsCard({ team, status }) {
  const [subStatus, setSubStatus] = useState(null);
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [cycle, setCycle] = useState("yearly"); // "monthly" | "yearly"

  useEffect(() => {
    if (!team?.id) return;
    api
      .get(`/subscriptions/status?team_id=${team.id}`)
      .then(({ data }) => setSubStatus(data))
      .catch(() => setSubStatus(null));
  }, [team?.id]);

  const subscribe = async (plan, withTrial = false) => {
    setLoadingPlan(plan);
    // Google Ads conversion: begin_checkout (fire BEFORE the redirect so the
    // event has time to flush even if Stripe load races the unload).
    const VALUE_MAP = {
      pro_monthly: 19.90,
      pro_yearly: 191,
      club_monthly: 49.90,
      club_yearly: 479,
    };
    trackBeginCheckout({ value: VALUE_MAP[plan], currency: "EUR", plan });
    try {
      const payload = { plan, team_id: team.id };
      if (withTrial) payload.trial_days = 14;
      const { data } = await api.post("/subscriptions/checkout", payload);
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore avvio checkout");
      setLoadingPlan(null);
    }
  };

  const openPortal = async () => {
    setLoadingPlan("portal");
    try {
      const { data } = await api.post("/subscriptions/portal");
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore apertura portale");
      setLoadingPlan(null);
    }
  };

  const hasActiveStripeSub =
    subStatus?.stripe_subscription_id && ["active", "trialing", "past_due"].includes(subStatus?.subscription_status);

  return (
    <Card className="p-6 border-emerald-200 bg-gradient-to-br from-emerald-50/60 to-white" data-testid="stripe-subscriptions-card">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-bold uppercase tracking-widest text-emerald-700 bg-emerald-100 px-2 py-1 rounded-full">
              Nuovo
            </span>
            <h3 className="font-heading font-bold text-lg">Abbonamento ricorrente Stripe</h3>
          </div>
          <p className="text-sm text-slate-600 mt-1">
            Pagamento automatico mensile · Annulli quando vuoi · Cambio piano dal portale clienti.
          </p>
        </div>
      </div>

      {hasActiveStripeSub ? (
        <div className="space-y-3">
          <div className="p-4 rounded-xl bg-white border border-emerald-200">
            <div className="text-xs uppercase tracking-wider text-emerald-600 font-semibold mb-1">
              Stato abbonamento
            </div>
            <div className="font-heading font-bold text-lg capitalize">
              {subStatus.subscription_tier || "—"} · {subStatus.subscription_status}
            </div>
            {subStatus.subscription_current_period_end && (
              <div className="text-sm text-slate-600 mt-1">
                Prossimo rinnovo: <strong>{formatDate(subStatus.subscription_current_period_end)}</strong>
                {subStatus.subscription_cancel_at_period_end && (
                  <span className="ml-2 text-amber-700">· Annullamento programmato</span>
                )}
              </div>
            )}
          </div>
          <Button
            onClick={openPortal}
            disabled={loadingPlan === "portal"}
            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
            data-testid="open-stripe-portal"
          >
            {loadingPlan === "portal" ? "Apertura portale…" : "Gestisci abbonamento (fatture, carta, annulla)"}
            <ArrowRight size={14} className="ml-1.5" />
          </Button>
        </div>
      ) : (
        <>
          {/* Billing cycle toggle */}
          <div className="flex justify-center mb-4">
            <div
              className="inline-flex items-center rounded-full bg-white border border-slate-200 p-1 shadow-sm"
              data-testid="billing-cycle-toggle"
            >
              <button
                type="button"
                onClick={() => setCycle("monthly")}
                data-testid="billing-cycle-monthly"
                className={`px-4 py-1.5 text-sm font-semibold rounded-full transition ${
                  cycle === "monthly"
                    ? "bg-slate-900 text-white"
                    : "text-slate-600 hover:text-slate-900"
                }`}
              >
                Mensile
              </button>
              <button
                type="button"
                onClick={() => setCycle("yearly")}
                data-testid="billing-cycle-yearly"
                className={`px-4 py-1.5 text-sm font-semibold rounded-full transition flex items-center gap-2 ${
                  cycle === "yearly"
                    ? "bg-slate-900 text-white"
                    : "text-slate-600 hover:text-slate-900"
                }`}
              >
                Annuale
                <span className="text-[10px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-emerald-500 text-white">
                  -20%
                </span>
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="p-4 rounded-xl bg-white border border-slate-200">
              <div className="font-heading font-bold text-base">Pro</div>
              <div className="text-2xl font-heading font-bold text-emerald-700 my-1" data-testid="price-pro">
                {cycle === "yearly" ? (
                  <>
                    €191<span className="text-sm text-slate-500 font-normal">/anno</span>
                  </>
                ) : (
                  <>
                    €19,90<span className="text-sm text-slate-500 font-normal">/mese</span>
                  </>
                )}
              </div>
              {cycle === "yearly" && (
                <div className="text-xs text-emerald-600 font-medium mb-2">
                  ≈ €15,90/mese · risparmi €47,80/anno
                </div>
              )}
              <ul className="text-xs text-slate-600 space-y-1 mb-3">
                <li>Fino a 3 squadre · 120 giocatori</li>
                <li>Mister AI + Modulistica + Firma OTP</li>
                <li>QR presenze + Tornei</li>
              </ul>
              <div className="space-y-2">
                <Button
                  onClick={() => subscribe(cycle === "yearly" ? "pro_yearly" : "pro_monthly", true)}
                  disabled={!!loadingPlan}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
                  data-testid="subscribe-pro-trial"
                >
                  {loadingPlan?.startsWith("pro_") ? "…" : "Prova 14 giorni gratis"}
                </Button>
                <Button
                  onClick={() => subscribe(cycle === "yearly" ? "pro_yearly" : "pro_monthly", false)}
                  disabled={!!loadingPlan}
                  variant="outline"
                  size="sm"
                  className="w-full"
                  data-testid="subscribe-pro-now"
                >
                  Sottoscrivi subito
                </Button>
              </div>
            </div>
            <div className="p-4 rounded-xl bg-white border-2 border-emerald-300 relative">
              <div className="absolute -top-2.5 right-3 px-2 py-0.5 bg-emerald-500 text-white text-[10px] font-bold uppercase tracking-widest rounded-full">
                Top
              </div>
              <div className="font-heading font-bold text-base">Club</div>
              <div className="text-2xl font-heading font-bold text-emerald-700 my-1" data-testid="price-club">
                {cycle === "yearly" ? (
                  <>
                    €479<span className="text-sm text-slate-500 font-normal">/anno</span>
                  </>
                ) : (
                  <>
                    €49,90<span className="text-sm text-slate-500 font-normal">/mese</span>
                  </>
                )}
              </div>
              {cycle === "yearly" && (
                <div className="text-xs text-emerald-600 font-medium mb-2">
                  ≈ €39,90/mese · risparmi €119,80/anno
                </div>
              )}
              <ul className="text-xs text-slate-600 space-y-1 mb-3">
                <li>Squadre illimitate</li>
                <li>Tutto del Pro + sito pubblico</li>
                <li>Sponsor, magazzino, bilancio</li>
                <li>Supporto prioritario</li>
              </ul>
              <Button
                onClick={() => subscribe(cycle === "yearly" ? "club_yearly" : "club_monthly", false)}
                disabled={!!loadingPlan}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
                data-testid="subscribe-club"
              >
                {loadingPlan?.startsWith("club_") ? "…" : "Abbonati al Club"}
              </Button>
            </div>
          </div>
        </>
      )}
      <p className="text-[11px] text-slate-500 mt-3">
        Pagamento sicuro tramite Stripe · Codici promozionali supportati al checkout.
        {status === "free" && hasActiveStripeSub === false && (
          <> Compila la prova gratuita per attivare il piano senza addebito immediato.</>
        )}
      </p>
    </Card>
  );
}
